import React, { useState } from "react";
import ProductCard from "../ProductCard";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import VideoCardSkeleton from "../VideoCardSkeleton";
import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import ProductDetailsModal from "../productDetailsModal/ProductDetailsModal";
import SelectModal from "@/pages/ProductStructure/components/SelectModal/SelectModal";

import { differenceInDays, parseISO, format } from "date-fns";

export type FilterType = "all" | "ad" | "organic";

const ViewAllVideos = ({
  event,
  onBack,
}: {
  event: any;
  onBack: () => void;
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");

  // Format the date (2025-09-15 -> September 15, 2025)
  const formattedDate = event?.date
    ? format(parseISO(event.date), "MMMM d, yyyy")
    : "";

  // Calculate days remaining
  const daysRemaining =
    event?.date && differenceInDays(parseISO(event.date), new Date()) >= 0
      ? differenceInDays(parseISO(event.date), new Date())
      : null;

  // Flatten all videos
  const allVideos: VideoData[] = event?.videos
    ? [
        ...(event.videos.top_ads || []),
        ...(event.videos.competitor_ads || []),
        ...(event.videos.competitor_reels || []),
      ]
    : [];

  // Apply filter
  const filteredVideos = allVideos?.filter((video) => {
    if (filter === "all") return true;
    if (filter === "ad") return video.ad_or_organic === "Ad";
    if (filter === "organic") return video.ad_or_organic === "Organic";
    return true;
  });

  return (
    <section>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-medium text-center">Event 🗓</h3>
        <button
          onClick={onBack}
          className="text-sm font-medium text-[#344054] border border-[#E4E7EC] rounded-lg px-4 py-2 hover:bg-gray-50 transition"
        >
          ← Go Back
        </button>
      </div>

      {/* Event Info */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <div className="flex gap-x-3 flex-wrap">
            <p className="text-[15px] text-[#777777] bg-[#F3F3F3] px-[5px] py-[3.5px] rounded-lg">
              {formattedDate}
            </p>
            {daysRemaining !== null && (
              <p className="text-[#006DBA] bg-[#EDF7FF] py-1 px-4 rounded-[10px] text-[15px]">
                in <span className="font-semibold">{daysRemaining} days</span>
              </p>
            )}
          </div>
          <h3 className="text-xl font-medium leading-none tracking-normal mt-4 mb-3">
            {event?.event}
          </h3>
        </div>

        <div className="text-end">
          <CustomButton
            onClick={() => setIsSelectModalOpen(true)}
            variant="outline"
            border="1px solid #FF6600"
            borderRadius="12px"
            padding="10px"
            color="#FF6600"
            fontSize="16px"
            fontWeight="500"
            className="w-[216px] leading-6 tracking-[1.5%]"
          >
            Place Order
          </CustomButton>
        </div>
      </div>

      {/* Filtering */}
      <div className="flex gap-x-3 items-center">
        {[
          { label: "All", value: "all" },
          { label: "Organic ads", value: "organic" },
          { label: "Inorganic ads", value: "ad" },
        ].map(({ label, value }) => (
          <button
            key={value}
            onClick={() => setFilter(value as FilterType)}
            className={`text-xs leading-[18px] tracking-normal py-1.5 px-[14px] border rounded-lg transition ${
              filter === value
                ? "border-[#FF6600] bg-[#FFF5F0] text-[#FF6600] font-medium"
                : "border-[#E4E7EC] text-black hover:bg-gray-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* No Videos */}
      {filteredVideos?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 border border-dashed border-[#E4E7EC] rounded-lg bg-[#FAFAFA]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-gray-400 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8c.707 0 1.354.293 1.854.793l5.293 5.293a2 2 0 010 2.828l-5.293 5.293a2 2 0 01-2.828 0l-5.293-5.293a2 2 0 010-2.828l5.293-5.293A2.615 2.615 0 0112 8z"
            />
          </svg>
          <p className="text-gray-500 text-lg font-medium">
            No videos available for this filter.
          </p>
        </div>
      )}

      {/* Videos Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {filteredVideos?.map((video) => (
          <ProductCard
            key={video.id}
            video={video}
            currentlyPlaying={currentlyPlaying}
            setCurrentlyPlaying={setCurrentlyPlaying}
            setIsDetailsModalOpen={setIsDetailsModalOpen}
            setSelectedVideo={setSelectedVideo}
          />
        ))}
      </div>

      {/* Modals */}
      <Modal
        padding="12px"
        width="750px"
        isOpen={isDetailsModalOpen}
        setOpen={setIsDetailsModalOpen}
      >
        <ProductDetailsModal
          showGenerateScript={true}
          video={selectedVideo as VideoData}
          setIsDetailsModalOpen={setIsDetailsModalOpen}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>

      <Modal
        isOpen={isSelectModalOpen}
        setOpen={setIsSelectModalOpen}
        width="min(900px, 95vw)"
      >
        <SelectModal
          isOpen={isSelectModalOpen}
          setOpen={setIsSelectModalOpen}
        />
      </Modal>
    </section>
  );
};
export default ViewAllVideos;
